<script setup lang="ts">
import {computed, ref, toRef} from "vue";
import {ChevronLeft, ChevronRight, FileImage, FileX} from "@lucide/vue";
import {usePrescriptionPage} from "@/medication-checkup/composables/usePrescriptionPage";

/**
 * A scanned prescription drawn as large as its box, one page at a time:
 * the page itself, its loading and failure, and the controls that turn a
 * prescription of several pages — a pill under the page, or arrows in its
 * bottom corners with the page count in the top one.
 *
 * It opens on the first page; a parent showing another prescription in the
 * same place keys it by prescription so it starts over.
 */

const props = withDefaults(defineProps<{
    prescriptionKey: string;
    /**
     * Whether the page may be fetched — false holds it back, for a sheet not
     * scrolled into view yet.
     */
    wanted?: boolean;
    pager?: 'pill' | 'corners';
}>(), {
    wanted: true,
    pager: 'pill'
});

const page = ref(1);

const {src, pageCount, loading, failed, retry} = usePrescriptionPage(
    computed(() => props.prescriptionKey),
    page,
    toRef(props, 'wanted')
);

const turn = (direction: 1 | -1) => {
    const next = page.value + direction;

    if (next >= 1 && next <= pageCount.value) {
        page.value = next;
    }
};

const CORNER = 'absolute bottom-2 flex size-8 items-center justify-center rounded-full bg-primary text-white '
    + 'shadow-custom hover:brightness-110 disabled:cursor-wait';
</script>

<template>
  <div class="relative h-full w-full">
    <img
      v-if="src"
      :src="src"
      alt="Ordonnance"
      class="block h-full w-full object-contain"
      :class="{'opacity-60': loading}"
    >
    <div v-else-if="failed" class="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
      <FileX class="size-10 stroke-1" />
      <span class="text-xs">Ordonnance indisponible</span>
      <button
        type="button"
        class="text-xs font-semibold text-primary hover:underline"
        @click.stop="retry"
      >
        Réessayer
      </button>
    </div>
    <div v-else class="h-full w-full bg-gray-light" :class="{'animate-pulse': loading}">
      <FileImage class="h-full w-full stroke-[0.5] text-gray" />
    </div>

    <template v-if="pager === 'corners'">
      <span class="absolute right-1.5 top-1.5 flex items-center gap-1">
        <span
          v-if="pageCount > 1"
          class="rounded-full bg-white/90 px-2 py-0.5 text-[10.5px] font-bold tabular-nums text-primary shadow-sm"
        >
          {{ page }} / {{ pageCount }}
        </span>
        <slot name="corner" />
      </span>

      <button
        v-if="page > 1"
        type="button"
        title="Page précédente"
        :class="CORNER"
        class="left-2"
        :disabled="loading"
        @click.stop="turn(-1)"
      >
        <ChevronLeft class="size-5" />
      </button>
      <button
        v-if="page < pageCount"
        type="button"
        title="Page suivante"
        :class="CORNER"
        class="right-2"
        :disabled="loading"
        @click.stop="turn(1)"
      >
        <ChevronRight class="size-5" />
      </button>
    </template>

    <div
      v-else-if="pageCount > 1"
      class="absolute bottom-2.5 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-white px-2 py-1 shadow-custom"
      @click.stop
    >
      <button
        type="button"
        class="flex size-6 items-center justify-center text-primary disabled:opacity-30"
        :disabled="page === 1 || loading"
        title="Page précédente"
        @click="turn(-1)"
      >
        <ChevronLeft class="size-4" />
      </button>
      <span class="text-[11.5px] tabular-nums text-gray-dark">Page {{ page }} / {{ pageCount }}</span>
      <button
        type="button"
        class="flex size-6 items-center justify-center text-primary disabled:opacity-30"
        :disabled="page === pageCount || loading"
        title="Page suivante"
        @click="turn(1)"
      >
        <ChevronRight class="size-4" />
      </button>
    </div>
  </div>
</template>
